import type { OperationHistoryDto } from "jadro-pico/dtos/pico_com/OperationHistoryDto.ts";
import { OperationOutcomeEnum } from "jadro-pico/enums/common/OperationOutcomeEnum.ts";
import type { QueueTransactionCommandEnum } from "jadro-pico/enums/common/QueueTransactionCommandEnum.ts";
import type { PendingMutation } from "./pendingMutationsDb.ts";

export enum RequestQueueStateEnum {
  WAITING_TO_SEND = "WAITING_TO_SEND",
  SENDING = "SENDING",
  AWAITING_WRITE = "AWAITING_WRITE",
  NOT_SENT = "NOT_SENT",
  NOT_STORED = "NOT_STORED",
  STORED = "STORED",
}

export interface RequestQueueItem {
  operationUuid: string;
  label?: string;
  command?: QueueTransactionCommandEnum;
  state: RequestQueueStateEnum;
  at: number;
}

export function historyRowToQueueItem(row: OperationHistoryDto): RequestQueueItem {
  return {
    operationUuid: row.operationUuid,
    command: row.command,
    state: recordedState(row),
    at: Date.parse(row.createdAt),
  };
}

function recordedState(row: OperationHistoryDto): RequestQueueStateEnum {
  return row.outcome === OperationOutcomeEnum.PROCESSED
    ? RequestQueueStateEnum.STORED
    : RequestQueueStateEnum.NOT_STORED;
}

const stateByOutboxStatus: Record<
  Exclude<PendingMutation["status"], "failed">,
  RequestQueueStateEnum
> = {
  queued: RequestQueueStateEnum.WAITING_TO_SEND,
  sending: RequestQueueStateEnum.SENDING,
  awaiting: RequestQueueStateEnum.AWAITING_WRITE,
};

// The history is authoritative for what happened; the outbox is authoritative for what is still
// held. An operation in both is one row, newest first.
export function mergeRequestQueue(
  outbox: PendingMutation[],
  history: OperationHistoryDto[],
): RequestQueueItem[] {
  const recorded = new Map(history.map((row) => [row.operationUuid, row] as const));

  const items: RequestQueueItem[] = outbox.map((entry) => ({
    operationUuid: entry.operationUuid,
    label: entry.label,
    command: recorded.get(entry.operationUuid)?.command,
    state: stateOf(entry, recorded.get(entry.operationUuid)),
    at: entry.createdAt,
  }));

  const locallyHeld = new Set(outbox.map((entry) => entry.operationUuid));
  for (const row of history) {
    if (locallyHeld.has(row.operationUuid)) continue;
    items.push(historyRowToQueueItem(row));
  }

  return items.sort((a, b) => b.at - a.at);
}

function stateOf(
  entry: PendingMutation,
  recorded: OperationHistoryDto | undefined,
): RequestQueueStateEnum {
  if (recorded) return recordedState(recorded);
  if (entry.status === "failed") return RequestQueueStateEnum.NOT_SENT;
  return stateByOutboxStatus[entry.status];
}
